import React from 'react';
import Header24 from './components/Header24';
import Footer24 from './components/Footer24';

const PrivacyPolicy24 = () => {
    const sections = [
        {
            title: 'Information We Collect',
            content: 'We collect information you provide directly, including your name, email address, phone number, shipping address, and payment information when you make a purchase or create an account. We also automatically collect certain technical information such as your IP address, browser type, and browsing behaviour through cookies and similar technologies.',
        },
        {
            title: 'How We Use Your Information',
            content: 'Your information is used to process and fulfil orders, communicate about your purchases, provide customer support, send marketing communications (with your consent), personalise your shopping experience, improve our website and services, and prevent fraud. We do not sell or rent your personal information to third parties.',
        },
        {
            title: 'Sharing Your Information',
            content: 'We may share your information with trusted service providers who assist us in operating our website, processing payments, shipping orders, and providing customer service. These providers are bound by confidentiality obligations. We may also share information when required by law or to protect our rights and the safety of our users.',
        },
        {
            title: 'Data Security',
            content: 'We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. Payment information is encrypted using industry-standard SSL technology. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.',
        },
        {
            title: 'Cookies',
            content: 'Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device that help us remember your preferences and understand how you use our site. You can control cookie settings through your browser, but disabling cookies may affect your ability to use certain features of our website.',
        },
        {
            title: 'Your Rights',
            content: 'You have the right to access, correct, or delete your personal information. You may also object to or restrict certain processing activities and request data portability. To exercise any of these rights, please contact us at privacy@dhara.in. We will respond to your request within 30 days.',
        },
        {
            title: 'Marketing Communications',
            content: 'With your consent, we may send you emails about new collections, special offers, and events. You can opt out of marketing communications at any time by clicking the unsubscribe link in our emails or contacting us directly. Opting out will not affect transactional communications related to your orders.',
        },
        {
            title: 'Third-Party Links',
            content: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices of these websites. We encourage you to review the privacy policies of any third-party sites you visit through links on our website.',
        },
        {
            title: 'Data Retention',
            content: 'We retain your personal information for as long as necessary to fulfil the purposes outlined in this policy, or as required by law. Account information is kept for the duration of your account activity. Transaction records are retained for a minimum of 7 years for legal and accounting purposes.',
        },
        {
            title: 'Updates to This Policy',
            content: 'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically. Your continued use of our website after any changes indicates acceptance of the updated policy.',
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-privacy {
                    min-height: 100vh;
                    background: #FFFFFF;
                }
                .dhara-privacy-inner {
                    max-width: 760px;
                    margin: 0 auto;
                    padding: 80px 40px 120px;
                }
                .dhara-privacy-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 42px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 16px;
                    letter-spacing: -0.5px;
                }
                .dhara-privacy-updated {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #6B6B6B;
                    margin-bottom: 40px;
                }
                .dhara-privacy-intro {
                    font-family: 'Inter', sans-serif;
                    font-size: 16px;
                    color: #6B6B6B;
                    line-height: 1.8;
                    font-weight: 300;
                    margin-bottom: 60px;
                    padding-bottom: 40px;
                    border-bottom: 1px solid #E5E5E5;
                }
                .dhara-privacy-section {
                    margin-bottom: 48px;
                }
                .dhara-privacy-section-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 22px;
                    font-weight: 500;
                    color: #1A1A1A;
                    margin-bottom: 16px;
                }
                .dhara-privacy-section-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    color: #6B6B6B;
                    line-height: 1.9;
                    font-weight: 300;
                }
                .dhara-privacy-contact {
                    margin-top: 60px;
                    padding: 40px;
                    background: #FAFAF8;
                    border: 1px solid #E5E5E5;
                }
                .dhara-privacy-contact-title {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    font-weight: 500;
                    color: #1A1A1A;
                    margin-bottom: 8px;
                }
                .dhara-privacy-contact-text {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    line-height: 1.7;
                }

                @media (max-width: 768px) {
                    .dhara-privacy-inner { padding: 40px 20px 80px; }
                    .dhara-privacy-title { font-size: 32px; }
                    .dhara-privacy-contact { padding: 24px; }
                }
            `}</style>

            <Header24 />

            <main className="dhara-privacy">
                <div className="dhara-privacy-inner">
                    <h1 className="dhara-privacy-title">Privacy Policy</h1>
                    <p className="dhara-privacy-updated">Last updated: March 1, 2026</p>
                    <p className="dhara-privacy-intro">
                        At Dhara, we respect your privacy and are committed to protecting your personal data.
                        This policy explains how we collect, use, and safeguard your information when you
                        visit our website or make a purchase.
                    </p>

                    {sections.map((section, i) => (
                        <div key={i} className="dhara-privacy-section">
                            <h2 className="dhara-privacy-section-title">{section.title}</h2>
                            <p className="dhara-privacy-section-text">{section.content}</p>
                        </div>
                    ))}

                    <div className="dhara-privacy-contact">
                        <div className="dhara-privacy-contact-title">Privacy Inquiries</div>
                        <p className="dhara-privacy-contact-text">
                            For questions about this policy or to exercise your rights, contact our Data Protection Officer at privacy@dhara.in
                        </p>
                    </div>
                </div>
            </main>

            <Footer24 />
        </>
    );
};

export default PrivacyPolicy24;
